import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  BarChart3, FileText, TrendingUp, Calendar, Download, Filter, 
  Users, Ship, Clock, DollarSign, PieChart, ArrowUpRight, ArrowDownRight,
  FileSpreadsheet, FileDown, Printer, RefreshCw, Lock, Anchor, FileType
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Chart component for bar charts
const BarChartSimple = ({ data, dataKey, nameKey, color = '#3b82f6', height = 200 }) => {
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Veri yok</div>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  const chartHeight = height - 60; // Label ve değer için yer bırak
  
  return (
    <div style={{ height, display: 'flex', flexDirection: 'column' }}>
      {/* Y-axis labels */}
      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
        {/* Y-axis */}
        <div style={{ width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: '8px', fontSize: '0.7rem', color: '#94a3b8', textAlign: 'right' }}>
          <span>${(maxValue / 1000).toFixed(0)}K</span>
          <span>${((maxValue * 0.75) / 1000).toFixed(0)}K</span>
          <span>${((maxValue * 0.5) / 1000).toFixed(0)}K</span>
          <span>${((maxValue * 0.25) / 1000).toFixed(0)}K</span>
          <span>$0</span>
        </div>
        
        {/* Chart area */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '4px', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', paddingLeft: '8px', position: 'relative' }}>
          {/* Grid lines */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} style={{ borderBottom: '1px dashed #e2e8f0', width: '100%' }} />
            ))}
          </div>
          
          {/* Bars */}
          {data.map((item, idx) => {
            const value = item[dataKey] || 0;
            const barHeight = maxValue > 0 ? (value / maxValue) * chartHeight : 0;
            return (
              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative', zIndex: 1 }}>
                {value > 0 && (
                  <div style={{ fontSize: '0.65rem', color: '#1e293b', fontWeight: 600, marginBottom: '4px' }}>
                    ${(value / 1000).toFixed(0)}K
                  </div>
                )}
                <div 
                  style={{ 
                    width: '80%', 
                    height: `${Math.max(barHeight, value > 0 ? 8 : 0)}px`,
                    background: value > 0 ? `linear-gradient(180deg, ${color}, ${color}dd)` : 'transparent',
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: value > 0 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                  }} 
                  title={`${item[nameKey]}: $${value.toLocaleString()}`}
                />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* X-axis labels */}
      <div style={{ display: 'flex', marginLeft: '58px', marginTop: '8px' }}>
        {data.map((item, idx) => (
          <div key={idx} style={{ flex: 1, textAlign: 'center', fontSize: '0.75rem', color: '#475569', fontWeight: 500 }}>
            {item[nameKey]}
          </div>
        ))}
      </div>
    </div>
  );
};

// Donut chart for payment methods
const DonutChart = ({ data, size = 150 }) => {
  if (!data || data.length === 0) return null;
  
  const total = data.reduce((sum, d) => sum + (d.total || 0), 0);
  const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];
  let currentAngle = 0;
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        {data.map((item, idx) => {
          const percentage = total > 0 ? (item.total / total) * 100 : 0;
          const angle = (percentage / 100) * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          
          const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
          const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
          const x2 = 50 + 40 * Math.cos((startAngle + angle - 90) * Math.PI / 180);
          const y2 = 50 + 40 * Math.sin((startAngle + angle - 90) * Math.PI / 180);
          const largeArc = angle > 180 ? 1 : 0;
          
          return (
            <path
              key={idx}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={colors[idx % colors.length]}
              stroke="#fff"
              strokeWidth="1"
            />
          );
        })}
        <circle cx="50" cy="50" r="25" fill="white" />
        <text x="50" y="50" textAnchor="middle" dy="0.35em" fontSize="10" fontWeight="bold" fill="#1e293b">
          ${(total / 1000).toFixed(0)}K
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {data.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
            <div style={{ width: 12, height: 12, borderRadius: '2px', background: colors[idx % colors.length] }} />
            <span style={{ color: '#64748b' }}>{item.name}: </span>
            <span style={{ fontWeight: 600 }}>${item.total?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Raporlama = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const reportContainerRef = useRef(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Year start
    end: new Date().toISOString().split('T')[0]
  });
  const [quickPeriod, setQuickPeriod] = useState('year');
  
  // Check user permission for reports
  const hasReportsPermission = user?.module_permissions?.reports === true;
  
  // Report data states
  const [stats, setStats] = useState(null);
  const [clientRevenue, setClientRevenue] = useState([]);
  const [vesselRevenue, setVesselRevenue] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [paymentPerformance, setPaymentPerformance] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [motorLaunchReport, setMotorLaunchReport] = useState(null);
  const [profitLossReport, setProfitLossReport] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const setQuickDateRange = (period) => {
    const today = new Date();
    let start;
    
    switch (period) {
      case 'week':
        start = new Date(today.setDate(today.getDate() - 7));
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
    }
    
    setQuickPeriod(period);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    });
  };

  const loadAllReports = useCallback(async () => {
    setLoading(true);
    try {
      // Cache-busting için timestamp ekle
      const cacheBuster = `_t=${Date.now()}`;
      const [
        dashboardRes,
        clientRevenueRes,
        vesselRevenueRes,
        agingRes,
        performanceRes,
        monthlyRes,
        motorRes,
        profitLossRes
      ] = await Promise.all([
        api.get(`/dashboard?${cacheBuster}`),
        api.get(`/reports/client-revenue?start_date=${dateRange.start}&end_date=${dateRange.end}&${cacheBuster}`),
        api.get(`/reports/vessel-revenue?start_date=${dateRange.start}&end_date=${dateRange.end}&${cacheBuster}`),
        api.get(`/reports/invoice-aging?${cacheBuster}`),
        api.get(`/reports/payment-performance?months=12&${cacheBuster}`),
        api.get(`/dashboard/monthly-revenue?months=12&${cacheBuster}`),
        api.get(`/reports/motor-launches?start_date=${dateRange.start}&end_date=${dateRange.end}&${cacheBuster}`),
        api.get(`/reports/profit-loss?start_date=${dateRange.start}&end_date=${dateRange.end}&${cacheBuster}`)
      ]);

      setStats(dashboardRes.data);
      setClientRevenue(clientRevenueRes.data);
      setVesselRevenue(vesselRevenueRes.data);
      setAgingReport(agingRes.data);
      setPaymentPerformance(performanceRes.data);
      setMonthlyRevenue(monthlyRes.data);
      setMotorLaunchReport(motorRes.data);
      setProfitLossReport(profitLossRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Rapor yükleme hatası:', error);
      toast.error('Rapor verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAllReports();
  }, [loadAllReports]);

  // Sayfa görünür olduğunda verileri yenile (otomatik güncelleme)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Sayfa görünür oldu, veriler yenileniyor...');
        loadAllReports();
      }
    };
    
    // Focus event'i de dinle (tab değişikliklerinde)
    const handleFocus = () => {
      console.log('Sayfa focus aldı, veriler yenileniyor...');
      loadAllReports();
    };
    
    // Popstate event'i (geri/ileri navigasyonu)
    const handlePopState = () => {
      console.log('Navigasyon değişti, veriler yenileniyor...');
      loadAllReports();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('popstate', handlePopState);
    
    // Her 30 saniyede bir otomatik yenile (sayfa açıkken)
    const autoRefreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('Otomatik yenileme...');
        loadAllReports();
      }
    }, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('popstate', handlePopState);
      clearInterval(autoRefreshInterval);
    };
  }, [loadAllReports]);

  // Export functions
  const exportToExcel = (reportType) => {
    let csvContent = '';
    let filename = '';
    
    switch (reportType) {
      case 'client':
        csvContent = [
          ['Müşteri Bazlı Gelir Raporu'],
          [`Dönem: ${dateRange.start} - ${dateRange.end}`],
          [''],
          ['Müşteri', 'Fatura Sayısı', 'Toplam Gelir ($)', 'Tahsil Edilen ($)', 'Bakiye ($)', 'Tahsilat Oranı (%)'],
          ...clientRevenue.map(c => [c.client_name, c.invoice_count, c.total_revenue, c.total_paid, c.total_balance, c.collection_rate])
        ].map(row => row.join(',')).join('\n');
        filename = `musteri_raporu_${dateRange.start}_${dateRange.end}.csv`;
        break;
        
      case 'vessel':
        csvContent = [
          ['Gemi Bazlı Gelir Raporu'],
          [`Dönem: ${dateRange.start} - ${dateRange.end}`],
          [''],
          ['Gemi Adı', 'Fatura Sayısı', 'Toplam Gelir ($)', 'Müşteri Sayısı'],
          ...vesselRevenue.map(v => [v.vessel_name, v.invoice_count, v.total_revenue, v.client_count])
        ].map(row => row.join(',')).join('\n');
        filename = `gemi_raporu_${dateRange.start}_${dateRange.end}.csv`;
        break;
        
      case 'aging':
        const agingRows = [];
        Object.entries(agingReport || {}).forEach(([key, bucket]) => {
          agingRows.push([bucket.label, bucket.count, bucket.total]);
          bucket.invoices?.forEach(inv => {
            agingRows.push(['  ' + inv.number, inv.client_name, inv.balance, inv.days_overdue + ' gün']);
          });
        });
        csvContent = [
          ['Fatura Yaşlandırma Raporu'],
          [new Date().toLocaleDateString('tr-TR')],
          [''],
          ['Kategori', 'Fatura Sayısı', 'Toplam Bakiye ($)'],
          ...Object.values(agingReport || {}).map(b => [b.label, b.count, b.total])
        ].map(row => row.join(',')).join('\n');
        filename = `yaslandirma_raporu_${new Date().toISOString().split('T')[0]}.csv`;
        break;
        
      case 'performance':
        csvContent = [
          ['Ödeme Performans Raporu'],
          [''],
          ['Ay', 'Faturalanan ($)', 'Tahsil Edilen ($)', 'Bekleyen ($)', 'Tahsilat Oranı (%)'],
          ...(paymentPerformance?.monthly_data || []).map(m => [m.month_name, m.invoiced, m.collected, m.outstanding, m.collection_rate])
        ].map(row => row.join(',')).join('\n');
        filename = `odeme_performans_raporu.csv`;
        break;
        
      default:
        return;
    }
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    toast.success('Excel raporu indirildi');
  };

  // Tüm raporları tek PDF olarak dışa aktar
  const exportFullPdfReport = async () => {
    setGeneratingPdf(true);
    toast.info('PDF raporu oluşturuluyor, lütfen bekleyin...');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let currentY = margin;
      
      // Türkçe karakter dönüştürücü
      const toAscii = (text) => {
        if (!text) return '';
        return String(text)
          .replace(/ş/g, 's').replace(/Ş/g, 'S')
          .replace(/ı/g, 'i').replace(/İ/g, 'I')
          .replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
          .replace(/ü/g, 'u').replace(/Ü/g, 'U')
          .replace(/ö/g, 'o').replace(/Ö/g, 'O')
          .replace(/ç/g, 'c').replace(/Ç/g, 'C');
      };
      
      // Helper: Yeni sayfa ekle
      const addNewPage = () => {
        pdf.addPage();
        currentY = margin;
      };
      
      // Helper: Başlık ekle
      const addSectionTitle = (title, color = [59, 130, 246]) => {
        if (currentY > pageHeight - 40) addNewPage();
        pdf.setFillColor(...color);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(toAscii(title), margin + 5, currentY + 7);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        currentY += 15;
      };
      
      // Helper: Tablo ekle
      const addTable = (headers, rows, colWidths) => {
        const rowHeight = 8;
        const tableWidth = pageWidth - 2 * margin;
        
        // Header
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, currentY, tableWidth, rowHeight, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        let xPos = margin + 2;
        headers.forEach((h, i) => {
          pdf.text(toAscii(h), xPos, currentY + 5.5);
          xPos += colWidths[i];
        });
        currentY += rowHeight;
        
        // Rows
        pdf.setFont('helvetica', 'normal');
        rows.forEach((row, rowIdx) => {
          if (currentY > pageHeight - 20) {
            addNewPage();
            // Re-add header
            pdf.setFillColor(241, 245, 249);
            pdf.rect(margin, currentY, tableWidth, rowHeight, 'F');
            pdf.setFont('helvetica', 'bold');
            xPos = margin + 2;
            headers.forEach((h, i) => {
              pdf.text(toAscii(h), xPos, currentY + 5.5);
              xPos += colWidths[i];
            });
            currentY += rowHeight;
            pdf.setFont('helvetica', 'normal');
          }
          
          if (rowIdx % 2 === 0) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, currentY, tableWidth, rowHeight, 'F');
          }
          
          xPos = margin + 2;
          row.forEach((cell, i) => {
            const cellText = toAscii(String(cell || '-')).substring(0, 30);
            pdf.text(cellText, xPos, currentY + 5.5);
            xPos += colWidths[i];
          });
          currentY += rowHeight;
        });
        currentY += 5;
      };
      
      // Müşteri gelir verilerinden toplam hesapla
      const totalRevenue = clientRevenue?.reduce((sum, c) => sum + (c.total_revenue || 0), 0) || 0;
      const totalCollected = clientRevenue?.reduce((sum, c) => sum + (c.total_paid || 0), 0) || 0;
      
      // ===== KAPAK SAYFASI =====
      pdf.setFillColor(30, 41, 59);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FINANSAL RAPOR', pageWidth / 2, 80, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Detayli Sirket Analizi', pageWidth / 2, 95, { align: 'center' });
      
      pdf.setFontSize(12);
      const periodText = toAscii(`Donem: ${dateRange.start} - ${dateRange.end}`);
      pdf.text(periodText, pageWidth / 2, 120, { align: 'center' });
      
      const generatedDate = toAscii(`Olusturulma: ${new Date().toLocaleDateString('tr-TR')}`);
      pdf.text(generatedDate, pageWidth / 2, 135, { align: 'center' });
      
      // Özet kutuları
      const summaryY = 160;
      const boxWidth = 40;
      const boxGap = 10;
      const startX = (pageWidth - (4 * boxWidth + 3 * boxGap)) / 2;
      
      const summaryData = [
        { label: 'Bekleyen Fatura', value: stats?.pending_invoices || 0, color: [59, 130, 246] },
        { label: 'Toplam Gelir', value: `$${(totalRevenue / 1000).toFixed(0)}K`, color: [16, 185, 129] },
        { label: 'Tahsil Edilen', value: `$${(totalCollected / 1000).toFixed(0)}K`, color: [139, 92, 246] },
        { label: 'Aktif Musteri', value: stats?.total_clients || 0, color: [245, 158, 11] }
      ];
      
      summaryData.forEach((item, i) => {
        const x = startX + i * (boxWidth + boxGap);
        pdf.setFillColor(...item.color);
        pdf.roundedRect(x, summaryY, boxWidth, 30, 3, 3, 'F');
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(item.value), x + boxWidth / 2, summaryY + 13, { align: 'center' });
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(toAscii(item.label), x + boxWidth / 2, summaryY + 22, { align: 'center' });
      });
      
      // ===== SAYFA 2: MÜŞTERİ ANALİZİ =====
      addNewPage();
      pdf.setTextColor(0, 0, 0);
      addSectionTitle('MUSTERI BAZLI GELIR ANALIZI', [59, 130, 246]);
      
      if (clientRevenue && clientRevenue.length > 0) {
        const clientHeaders = ['Musteri', 'Fatura', 'Gelir', 'Odenen', 'Bakiye', 'Tahsilat%'];
        const clientRows = clientRevenue.slice(0, 15).map(c => [
          (c.client_name || '').substring(0, 25),
          c.invoice_count,
          `$${(c.total_revenue || 0).toLocaleString()}`,
          `$${(c.total_paid || 0).toLocaleString()}`,
          `$${(c.total_balance || 0).toLocaleString()}`,
          `%${c.collection_rate || 0}`
        ]);
        addTable(clientHeaders, clientRows, [50, 20, 30, 30, 30, 20]);
      }
      
      // ===== SAYFA 3: GEMİ ANALİZİ =====
      addNewPage();
      addSectionTitle('GEMI BAZLI GELIR ANALIZI', [16, 185, 129]);
      
      if (vesselRevenue && vesselRevenue.length > 0) {
        const vesselHeaders = ['Gemi Adi', 'Fatura Sayisi', 'Toplam Gelir', 'Musteri Sayisi'];
        const vesselRows = vesselRevenue.slice(0, 15).map(v => [
          (v.vessel_name || '').substring(0, 30),
          v.invoice_count,
          `$${(v.total_revenue || 0).toLocaleString()}`,
          v.client_count
        ]);
        addTable(vesselHeaders, vesselRows, [60, 30, 45, 35]);
      }
      
      // ===== SAYFA 4: FATURA YAŞLANDIRMA =====
      addNewPage();
      addSectionTitle('FATURA YASLANDIRMA RAPORU', [239, 68, 68]);
      
      if (agingReport) {
        const agingHeaders = ['Vade Araligi', 'Fatura Sayisi', 'Toplam Tutar'];
        const agingRows = Object.entries(agingReport).map(([key, data]) => [
          data.label,
          data.count,
          `$${(data.total || 0).toLocaleString()}`
        ]);
        addTable(agingHeaders, agingRows, [60, 50, 60]);
      }
      
      // ===== SAYFA 5: ÖDEME PERFORMANSI =====
      addNewPage();
      addSectionTitle('AYLIK ODEME PERFORMANSI', [139, 92, 246]);
      
      if (paymentPerformance?.monthly_data) {
        const perfHeaders = ['Ay', 'Faturalanan', 'Tahsil Edilen', 'Bakiye', 'Tahsilat%'];
        const perfRows = paymentPerformance.monthly_data.slice(0, 12).map(m => [
          m.month_name,
          `$${(m.invoiced || 0).toLocaleString()}`,
          `$${(m.collected || 0).toLocaleString()}`,
          `$${(m.outstanding || 0).toLocaleString()}`,
          `%${m.collection_rate || 0}`
        ]);
        addTable(perfHeaders, perfRows, [40, 35, 35, 35, 25]);
      }
      
      // ===== SAYFA 6: MOTOR SEFERLERİ =====
      addNewPage();
      addSectionTitle('MOTOR SEFERLERI RAPORU', [245, 158, 11]);
      
      if (motorLaunchReport?.summary) {
        currentY += 5;
        pdf.setFontSize(10);
        pdf.text(toAscii(`Toplam Sefer: ${motorLaunchReport.summary.total_trips}`), margin, currentY);
        pdf.text(toAscii(`Levend: ${motorLaunchReport.summary.levend_trips} sefer - $${(motorLaunchReport.summary.levend_earnings || 0).toLocaleString()}`), margin, currentY + 8);
        pdf.text(toAscii(`Rota: ${motorLaunchReport.summary.rota_trips} sefer - $${(motorLaunchReport.summary.rota_earnings || 0).toLocaleString()}`), margin, currentY + 16);
        pdf.text(toAscii(`Toplam Gelir: $${(motorLaunchReport.summary.total_earnings || 0).toLocaleString()}`), margin, currentY + 24);
        currentY += 35;
      }
      
      if (motorLaunchReport?.monthly_data) {
        const motorHeaders = ['Ay', 'Levend', 'Rota', 'Toplam'];
        const motorRows = motorLaunchReport.monthly_data.slice(0, 12).map(m => [
          m.month_name,
          `$${(m.levend_earnings || 0).toLocaleString()}`,
          `$${(m.rota_earnings || 0).toLocaleString()}`,
          `$${(m.total_earnings || 0).toLocaleString()}`
        ]);
        addTable(motorHeaders, motorRows, [50, 40, 40, 40]);
      }
      
      // ===== SAYFA 7: KAR/ZARAR ANALİZİ =====
      addNewPage();
      addSectionTitle('KAR/ZARAR ANALIZI', [220, 38, 38]);
      
      if (profitLossReport) {
        currentY += 5;
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(toAscii('Genel Ozet:'), margin, currentY);
        pdf.setFont('helvetica', 'normal');
        currentY += 10;
        
        pdf.setFontSize(10);
        pdf.text(toAscii(`Toplam Gelir: $${(profitLossReport.total_revenue || 0).toLocaleString()}`), margin, currentY);
        pdf.text(toAscii(`Toplam Maliyet: $${(profitLossReport.total_cost || 0).toLocaleString()}`), margin + 60, currentY);
        currentY += 8;
        pdf.text(toAscii(`Toplam Kar: $${(profitLossReport.total_profit || 0).toLocaleString()}`), margin, currentY);
        pdf.text(toAscii(`Kar Marji: %${(profitLossReport.profit_margin_percentage || 0).toFixed(1)}`), margin + 60, currentY);
        currentY += 8;
        pdf.text(toAscii(`Odenen Maliyet: $${(profitLossReport.total_paid_cost || 0).toLocaleString()}`), margin, currentY);
        pdf.text(toAscii(`Odenecek Maliyet: $${(profitLossReport.total_pending_cost || 0).toLocaleString()}`), margin + 60, currentY);
        currentY += 15;
        
        if (profitLossReport.by_category && profitLossReport.by_category.length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Kategori Bazli:', margin, currentY);
          pdf.setFont('helvetica', 'normal');
          currentY += 8;
          
          const catHeaders = ['Kategori', 'Gelir', 'Maliyet', 'Kar', 'Islem'];
          const catRows = profitLossReport.by_category.slice(0, 10).map(c => [
            (c.category_name || c.category || '').substring(0, 25),
            `$${(c.revenue || 0).toLocaleString()}`,
            `$${(c.cost || 0).toLocaleString()}`,
            `$${(c.profit || 0).toLocaleString()}`,
            c.count
          ]);
          addTable(catHeaders, catRows, [50, 35, 35, 35, 20]);
        }
      }
      
      // ===== SON SAYFA: FOOTER =====
      addNewPage();
      pdf.setFillColor(241, 245, 249);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(12);
      pdf.text(toAscii('Bu rapor otomatik olarak olusturulmustur.'), pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });
      pdf.text(toAscii(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')} ${new Date().toLocaleTimeString('tr-TR')}`), pageWidth / 2, pageHeight / 2, { align: 'center' });
      pdf.text(toAscii(`Donem: ${dateRange.start} - ${dateRange.end}`), pageWidth / 2, pageHeight / 2 + 20, { align: 'center' });
      
      // PDF'i kaydet
      const fileName = `sirket_raporu_${dateRange.start}_${dateRange.end}.pdf`;
      pdf.save(fileName);
      toast.success('PDF raporu başarıyla oluşturuldu!');
      
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      toast.error('PDF oluşturulamadı: ' + error.message);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Genel Bakış', icon: PieChart },
    { id: 'clients', label: 'Müşteri Analizi', icon: Users },
    { id: 'vessels', label: 'Gemi Analizi', icon: Ship },
    { id: 'aging', label: 'Fatura Yaşlandırma', icon: Clock },
    { id: 'performance', label: 'Ödeme Performansı', icon: TrendingUp },
    { id: 'motor_launches', label: 'Motor Seferleri', icon: Anchor },
    { id: 'profit_loss', label: 'Kar/Zarar Analizi', icon: DollarSign }
  ];

  if (loading) {
    return (
      <div className="content">
        <div className="topbar">
          <h1 className="topbar-title"><BarChart3 size={24} style={{ marginRight: '0.5rem' }} /> Raporlama</h1>
        </div>
        <div style={{ padding: '4rem', textAlign: 'center' }}>
          <RefreshCw size={48} className="animate-spin" style={{ color: '#3b82f6', marginBottom: '1rem' }} />
          <p style={{ color: '#64748b' }}>Raporlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Yetki kontrolü - Finansal detayları görmek için reports yetkisi gerekli
  if (!hasReportsPermission) {
    return (
      <div className="content">
        <div className="topbar">
          <h1 className="topbar-title">
            <BarChart3 size={24} style={{ marginRight: '0.5rem' }} />
            Raporlama
          </h1>
        </div>
        <div style={{ 
          padding: '4rem', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
            borderRadius: '50%',
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1.5rem'
          }}>
            <Lock size={48} style={{ color: '#dc2626' }} />
          </div>
          <h2 style={{ color: '#1e293b', marginBottom: '0.5rem', fontSize: '1.5rem' }}>Erişim Kısıtlı</h2>
          <p style={{ color: '#64748b', maxWidth: '400px', lineHeight: 1.6 }}>
            Finansal raporları görüntülemek için yetkiniz bulunmamaktadır. 
            Yetki almak için sistem yöneticinizle iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title">
          <BarChart3 size={24} style={{ marginRight: '0.5rem' }} />
          Raporlama
        </h1>
        <div className="topbar-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-primary" 
            onClick={exportFullPdfReport} 
            disabled={generatingPdf}
            title="Tüm Raporları PDF Olarak İndir"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            data-testid="export-full-pdf-btn"
          >
            {generatingPdf ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                PDF Oluşturuluyor...
              </>
            ) : (
              <>
                <FileType size={18} />
                Tam Rapor (PDF)
              </>
            )}
          </button>
          <button className="btn btn-secondary" onClick={loadAllReports} title="Verileri Yenile" disabled={loading}>
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Son Güncelleme Bilgisi */}
      {lastUpdated && (
        <div style={{ 
          padding: '0 1.5rem', 
          marginBottom: '-0.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            Son güncelleme: {lastUpdated.toLocaleTimeString('tr-TR')}
          </span>
          <button 
            onClick={loadAllReports} 
            disabled={loading}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              color: '#3b82f6',
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>
      )}

      <div style={{ padding: '1.5rem' }}>
        {/* Date Range Filter */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-body" style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { id: 'week', label: 'Son 7 Gün' },
                  { id: 'month', label: 'Bu Ay' },
                  { id: 'quarter', label: 'Bu Çeyrek' },
                  { id: 'year', label: 'Bu Yıl' }
                ].map(p => (
                  <button
                    key={p.id}
                    className={`btn ${quickPeriod === p.id ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setQuickDateRange(p.id)}
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={16} style={{ color: '#64748b' }} />
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.start}
                    onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setQuickPeriod(''); }}
                    style={{ padding: '0.4rem', fontSize: '0.85rem' }}
                  />
                  <span style={{ color: '#94a3b8' }}>-</span>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.end}
                    onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setQuickPeriod(''); }}
                    style={{ padding: '0.4rem', fontSize: '0.85rem' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                // Kar/Zarar sekmesine geçişte verileri yenile
                if (tab.id === 'profit_loss') {
                  loadAllReports();
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.25rem', border: 'none', borderRadius: '8px 8px 0 0',
                background: activeTab === tab.id ? '#3b82f6' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#64748b',
                fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Bekleyen Faturalar</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats?.pending_invoices || 0}</div>
                  </div>
                  <FileText size={32} style={{ opacity: 0.5 }} />
                </div>
              </div>
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Gecikmiş Faturalar</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats?.overdue_invoices || 0}</div>
                  </div>
                  <Clock size={32} style={{ opacity: 0.5 }} />
                </div>
              </div>
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Alacak</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>${(stats?.total_outstanding || 0).toLocaleString()}</div>
                  </div>
                  <DollarSign size={32} style={{ opacity: 0.5 }} />
                </div>
              </div>
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Tahsilat</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>${(clientRevenue?.reduce((sum, c) => sum + (c.total_paid || 0), 0) || 0).toLocaleString()}</div>
                  </div>
                  <TrendingUp size={32} style={{ opacity: 0.5 }} />
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="card-title" style={{ margin: 0 }}>Aylık Gelir Trendi</h3>
                </div>
                <div className="card-body">
                  <BarChartSimple 
                    data={monthlyRevenue?.filter(m => m.month?.includes('2026') || m.invoices > 0)} 
                    dataKey="invoices" 
                    nameKey="month" 
                    color="#3b82f6" 
                    height={280} 
                  />
                </div>
              </div>
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>Ödeme Yöntemleri</h3>
                </div>
                <div className="card-body" style={{ display: 'flex', justifyContent: 'center' }}>
                  <DonutChart data={paymentPerformance?.payment_methods || []} size={140} />
                </div>
              </div>
            </div>

            {/* Top Tables Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="card-title" style={{ margin: 0 }}>En Yüksek Cirolu Müşteriler</h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Müşteri</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Gelir</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tahsilat %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientRevenue.slice(0, 5).map((c, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}>{c.client_name}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>${c.total_revenue.toLocaleString()}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                            <span style={{ 
                              padding: '0.2rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.75rem',
                              background: c.collection_rate >= 80 ? '#dcfce7' : c.collection_rate >= 50 ? '#fef3c7' : '#fee2e2',
                              color: c.collection_rate >= 80 ? '#166534' : c.collection_rate >= 50 ? '#92400e' : '#991b1b'
                            }}>
                              {c.collection_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>Fatura Yaşlandırma Özeti</h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Dönem</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Adet</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agingReport && Object.entries(agingReport).map(([key, bucket]) => (
                        <tr key={key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}>{bucket.label}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{bucket.count}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: key === '90_plus' ? '#dc2626' : 'inherit' }}>
                            ${bucket.total.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ margin: 0 }}><Users size={18} style={{ marginRight: '0.5rem' }} />Müşteri Bazlı Gelir Raporu</h3>
              <button className="btn btn-success" onClick={() => exportToExcel('client')}>
                <FileSpreadsheet size={16} /> Excel İndir
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Müşteri</th>
                      <th style={{ textAlign: 'center' }}>Fatura</th>
                      <th style={{ textAlign: 'right' }}>Toplam Gelir</th>
                      <th style={{ textAlign: 'right' }}>Tahsil Edilen</th>
                      <th style={{ textAlign: 'right' }}>Bakiye</th>
                      <th style={{ textAlign: 'center' }}>Tahsilat Oranı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientRevenue.map((client, idx) => (
                      <tr key={idx}>
                        <td><strong>{client.client_name}</strong></td>
                        <td style={{ textAlign: 'center' }}>{client.invoice_count}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${client.total_revenue.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: '#059669' }}>${client.total_paid.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: client.total_balance > 0 ? '#dc2626' : '#059669' }}>
                          ${client.total_balance.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <div style={{ 
                              width: '60px', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' 
                            }}>
                              <div style={{ 
                                width: `${client.collection_rate}%`, height: '100%', 
                                background: client.collection_rate >= 80 ? '#10b981' : client.collection_rate >= 50 ? '#f59e0b' : '#ef4444',
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{client.collection_rate}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Toplam Satırı */}
                  {clientRevenue.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#1e293b', color: 'white', fontWeight: 700 }}>
                        <td style={{ padding: '0.75rem 1rem' }}>TOPLAM ({clientRevenue.length} Müşteri)</td>
                        <td style={{ textAlign: 'center', padding: '0.75rem 1rem' }}>
                          {clientRevenue.reduce((sum, c) => sum + c.invoice_count, 0)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.75rem 1rem' }}>
                          ${clientRevenue.reduce((sum, c) => sum + c.total_revenue, 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#4ade80' }}>
                          ${clientRevenue.reduce((sum, c) => sum + c.total_paid, 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#fca5a5' }}>
                          ${clientRevenue.reduce((sum, c) => sum + c.total_balance, 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'center', padding: '0.75rem 1rem' }}>
                          {(() => {
                            const totalRevenue = clientRevenue.reduce((sum, c) => sum + c.total_revenue, 0);
                            const totalPaid = clientRevenue.reduce((sum, c) => sum + c.total_paid, 0);
                            const avgRate = totalRevenue > 0 ? Math.round((totalPaid / totalRevenue) * 100) : 0;
                            return `${avgRate}%`;
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'vessels' && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ margin: 0 }}><Ship size={18} style={{ marginRight: '0.5rem' }} />Gemi Bazlı Gelir Raporu</h3>
              <button className="btn btn-success" onClick={() => exportToExcel('vessel')}>
                <FileSpreadsheet size={16} /> Excel İndir
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Gemi Adı</th>
                      <th style={{ textAlign: 'center' }}>Fatura Sayısı</th>
                      <th style={{ textAlign: 'right' }}>Toplam Gelir</th>
                      <th style={{ textAlign: 'center' }}>Müşteri Sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vesselRevenue.map((vessel, idx) => (
                      <tr key={idx}>
                        <td><strong>{vessel.vessel_name}</strong></td>
                        <td style={{ textAlign: 'center' }}>{vessel.invoice_count}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#3b82f6' }}>${vessel.total_revenue.toLocaleString()}</td>
                        <td style={{ textAlign: 'center' }}>{vessel.client_count}</td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Toplam Satırı */}
                  {vesselRevenue.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#1e293b', color: 'white', fontWeight: 700 }}>
                        <td style={{ padding: '0.75rem 1rem' }}>TOPLAM ({vesselRevenue.length} Gemi)</td>
                        <td style={{ textAlign: 'center', padding: '0.75rem 1rem' }}>
                          {vesselRevenue.reduce((sum, v) => sum + v.invoice_count, 0)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.75rem 1rem', color: '#60a5fa' }}>
                          ${vesselRevenue.reduce((sum, v) => sum + v.total_revenue, 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'center', padding: '0.75rem 1rem' }}>
                          {new Set(vesselRevenue.flatMap(v => v.clients || [])).size || vesselRevenue.reduce((sum, v) => sum + v.client_count, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'aging' && (
          <div>
            {/* Aging Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {agingReport && Object.entries(agingReport).map(([key, bucket]) => {
                const colors = {
                  current: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
                  '1_30': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
                  '31_60': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
                  '61_90': { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
                  '90_plus': { bg: '#fecaca', text: '#7f1d1d', border: '#f87171' }
                };
                const color = colors[key] || colors.current;
                
                return (
                  <div key={key} style={{ 
                    background: color.bg, 
                    border: `2px solid ${color.border}`,
                    borderRadius: '12px', 
                    padding: '1.25rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.85rem', color: color.text, marginBottom: '0.5rem' }}>{bucket.label}</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: color.text }}>{bucket.count}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: color.text }}>${bucket.total.toLocaleString()}</div>
                  </div>
                );
              })}
            </div>

            {/* Detailed Aging Table */}
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ margin: 0 }}><Clock size={18} style={{ marginRight: '0.5rem' }} />Detaylı Yaşlandırma Raporu</h3>
                <button className="btn btn-success" onClick={() => exportToExcel('aging')}>
                  <FileSpreadsheet size={16} /> Excel İndir
                </button>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {agingReport && Object.entries(agingReport).map(([key, bucket]) => (
                  bucket.invoices?.length > 0 && (
                    <div key={key} style={{ marginBottom: '1rem' }}>
                      <div style={{ 
                        background: '#f8fafc', 
                        padding: '0.75rem 1rem', 
                        fontWeight: 600,
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>{bucket.label}</span>
                        <span>{bucket.count} fatura - ${bucket.total.toLocaleString()}</span>
                      </div>
                      <table style={{ width: '100%', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={{ padding: '0.5rem 1rem' }}>Fatura No</th>
                            <th style={{ padding: '0.5rem 1rem' }}>Müşteri</th>
                            <th style={{ padding: '0.5rem 1rem' }}>Vade Tarihi</th>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Bakiye</th>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>Gecikme</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bucket.invoices.slice(0, 10).map((inv, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '0.5rem 1rem' }}><strong>{inv.number}</strong></td>
                              <td style={{ padding: '0.5rem 1rem' }}>{inv.client_name}</td>
                              <td style={{ padding: '0.5rem 1rem' }}>{new Date(inv.due_date).toLocaleDateString('tr-TR')}</td>
                              <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: 600 }}>{inv.currency} {inv.balance.toLocaleString()}</td>
                              <td style={{ padding: '0.5rem 1rem', textAlign: 'center', color: inv.days_overdue > 60 ? '#dc2626' : '#64748b' }}>
                                {inv.days_overdue > 0 ? `${inv.days_overdue} gün` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div>
            {/* Performance Summary */}
            {paymentPerformance?.summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="stat-card">
                  <div className="stat-card-title">Toplam Faturalanan</div>
                  <div className="stat-card-value" style={{ color: '#3b82f6' }}>${paymentPerformance.summary.total_invoiced.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-title">Toplam Tahsil Edilen</div>
                  <div className="stat-card-value" style={{ color: '#10b981' }}>${paymentPerformance.summary.total_collected.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-title">Toplam Bekleyen</div>
                  <div className="stat-card-value" style={{ color: '#f59e0b' }}>${paymentPerformance.summary.total_outstanding.toLocaleString()}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-title">Ortalama Tahsilat Oranı</div>
                  <div className="stat-card-value" style={{ color: paymentPerformance.summary.avg_collection_rate >= 70 ? '#10b981' : '#f59e0b' }}>
                    {paymentPerformance.summary.avg_collection_rate}%
                  </div>
                </div>
              </div>
            )}

            {/* Monthly Performance Table */}
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ margin: 0 }}><TrendingUp size={18} style={{ marginRight: '0.5rem' }} />Aylık Performans</h3>
                <button className="btn btn-success" onClick={() => exportToExcel('performance')}>
                  <FileSpreadsheet size={16} /> Excel İndir
                </button>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Ay</th>
                        <th style={{ textAlign: 'right' }}>Faturalanan</th>
                        <th style={{ textAlign: 'right' }}>Tahsil Edilen</th>
                        <th style={{ textAlign: 'right' }}>Bekleyen</th>
                        <th style={{ textAlign: 'center' }}>Tahsilat Oranı</th>
                        <th style={{ textAlign: 'center' }}>Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentPerformance?.monthly_data?.map((month, idx) => {
                        const prevMonth = paymentPerformance.monthly_data[idx - 1];
                        const trend = prevMonth ? month.collection_rate - prevMonth.collection_rate : 0;
                        
                        return (
                          <tr key={idx}>
                            <td><strong>{month.month_name}</strong></td>
                            <td style={{ textAlign: 'right' }}>${month.invoiced.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', color: '#059669' }}>${month.collected.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', color: month.outstanding > 0 ? '#dc2626' : '#059669' }}>
                              ${month.outstanding.toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <div style={{ 
                                  width: '80px', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' 
                                }}>
                                  <div style={{ 
                                    width: `${Math.min(month.collection_rate, 100)}%`, height: '100%', 
                                    background: month.collection_rate >= 80 ? '#10b981' : month.collection_rate >= 50 ? '#f59e0b' : '#ef4444'
                                  }} />
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, minWidth: '45px' }}>{month.collection_rate}%</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {trend !== 0 && (
                                <span style={{ 
                                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                  color: trend > 0 ? '#10b981' : '#ef4444', fontSize: '0.8rem'
                                }}>
                                  {trend > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                  {Math.abs(trend).toFixed(1)}%
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'motor_launches' && (
          <div>
            {/* Motor Seferleri Özet Kartları */}
            {motorLaunchReport?.summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Motor Geliri</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${motorLaunchReport.summary.total_earnings?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{motorLaunchReport.summary.total_trips} sefer</div>
                    </div>
                    <Anchor size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>LEVEND</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${motorLaunchReport.summary.levend_earnings?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{motorLaunchReport.summary.levend_trips} sefer</div>
                    </div>
                    <Ship size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>ROTA</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${motorLaunchReport.summary.rota_earnings?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{motorLaunchReport.summary.rota_trips} sefer</div>
                    </div>
                    <Ship size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
              </div>
            )}

            {/* Aylık Motor Gelirleri */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ margin: 0 }}>
                  <Anchor size={18} style={{ marginRight: '0.5rem' }} />
                  Aylık Motor Gelirleri
                </h3>
                <button 
                  className="btn btn-success" 
                  onClick={() => {
                    const csvContent = [
                      ['Motor Seferleri Raporu'],
                      [`Dönem: ${dateRange.start} - ${dateRange.end}`],
                      [''],
                      ['Ay', 'Levend Geliri ($)', 'Levend Sefer', 'Rota Geliri ($)', 'Rota Sefer', 'Toplam Gelir ($)', 'Toplam Sefer'],
                      ...(motorLaunchReport?.monthly_data || []).map(m => [
                        m.month_name, m.levend_earnings, m.levend_trips, m.rota_earnings, m.rota_trips, m.total_earnings, m.total_trips
                      ])
                    ].map(row => row.join(',')).join('\n');
                    
                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `motor_seferleri_${dateRange.start}_${dateRange.end}.csv`;
                    link.click();
                    toast.success('Excel raporu indirildi');
                  }}
                >
                  <FileSpreadsheet size={16} /> Excel İndir
                </button>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Ay</th>
                        <th style={{ textAlign: 'center', background: '#eff6ff', color: '#1d4ed8' }}>Levend Geliri</th>
                        <th style={{ textAlign: 'center', background: '#eff6ff', color: '#1d4ed8' }}>Levend Sefer</th>
                        <th style={{ textAlign: 'center', background: '#ecfdf5', color: '#047857' }}>Rota Geliri</th>
                        <th style={{ textAlign: 'center', background: '#ecfdf5', color: '#047857' }}>Rota Sefer</th>
                        <th style={{ textAlign: 'right' }}>Toplam Gelir</th>
                        <th style={{ textAlign: 'center' }}>Toplam Sefer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {motorLaunchReport?.monthly_data?.map((month, idx) => (
                        <tr key={idx}>
                          <td><strong>{month.month_name}</strong></td>
                          <td style={{ textAlign: 'center', background: '#f0f9ff' }}>
                            <span style={{ color: '#2563eb', fontWeight: 600 }}>${month.levend_earnings?.toLocaleString()}</span>
                          </td>
                          <td style={{ textAlign: 'center', background: '#f0f9ff' }}>{month.levend_trips}</td>
                          <td style={{ textAlign: 'center', background: '#f0fdf4' }}>
                            <span style={{ color: '#059669', fontWeight: 600 }}>${month.rota_earnings?.toLocaleString()}</span>
                          </td>
                          <td style={{ textAlign: 'center', background: '#f0fdf4' }}>{month.rota_trips}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                            ${month.total_earnings?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'center' }}>{month.total_trips}</td>
                        </tr>
                      ))}
                      {/* Toplam Satırı */}
                      {motorLaunchReport?.monthly_data?.length > 0 && (
                        <tr style={{ background: '#f8fafc', fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                          <td>TOPLAM</td>
                          <td style={{ textAlign: 'center', color: '#2563eb' }}>
                            ${motorLaunchReport.summary?.levend_earnings?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'center' }}>{motorLaunchReport.summary?.levend_trips}</td>
                          <td style={{ textAlign: 'center', color: '#059669' }}>
                            ${motorLaunchReport.summary?.rota_earnings?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'center' }}>{motorLaunchReport.summary?.rota_trips}</td>
                          <td style={{ textAlign: 'right', color: '#7c3aed', fontSize: '1.1rem' }}>
                            ${motorLaunchReport.summary?.total_earnings?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'center' }}>{motorLaunchReport.summary?.total_trips}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Gemi ve Müşteri Analizi */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Gemi Bazlı Motor Gelirleri */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>
                    <Ship size={18} style={{ marginRight: '0.5rem' }} />
                    Gemi Bazlı Motor Gelirleri
                  </h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Gemi</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#2563eb' }}>Levend</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>Rota</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Toplam</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Sefer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {motorLaunchReport?.top_vessels?.map((vessel, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}><strong>{vessel.vessel_name}</strong></td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#2563eb' }}>
                            {vessel.levend > 0 ? `$${vessel.levend.toLocaleString()}` : '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>
                            {vessel.rota > 0 ? `$${vessel.rota.toLocaleString()}` : '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                            ${vessel.total?.toLocaleString()}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>{vessel.trips}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Müşteri Bazlı Motor Gelirleri */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>
                    <Users size={18} style={{ marginRight: '0.5rem' }} />
                    Müşteri Bazlı Motor Gelirleri
                  </h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Müşteri</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#2563eb' }}>Levend</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>Rota</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Toplam</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Sefer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {motorLaunchReport?.top_clients?.map((client, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}><strong>{client.client_name}</strong></td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#2563eb' }}>
                            {client.levend > 0 ? `$${client.levend.toLocaleString()}` : '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>
                            {client.rota > 0 ? `$${client.rota.toLocaleString()}` : '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                            ${client.total?.toLocaleString()}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>{client.trips}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Veri yoksa mesaj */}
            {(!motorLaunchReport?.monthly_data || motorLaunchReport.monthly_data.length === 0) && (
              <div className="card" style={{ marginTop: '1.5rem' }}>
                <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
                  <Anchor size={48} style={{ color: '#94a3b8', marginBottom: '1rem' }} />
                  <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Seçili Dönemde Veri Bulunamadı</h3>
                  <p style={{ color: '#94a3b8' }}>
                    {dateRange.start} - {dateRange.end} tarihleri arasında motor seferi kaydı bulunmuyor.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* KAR/ZARAR ANALİZİ SEKMESİ */}
        {activeTab === 'profit_loss' && (
          <div data-testid="profit-loss-tab">
            {/* Özet Kartları */}
            {profitLossReport && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}>
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Satış</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${profitLossReport.total_revenue?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{profitLossReport.items_count} kalem</div>
                    </div>
                    <TrendingUp size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
                
                <div className="card" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white' }}>
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Maliyet</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${profitLossReport.total_cost?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>alış bedeli</div>
                    </div>
                    <ArrowDownRight size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
                
                <div className="card" style={{ background: `linear-gradient(135deg, ${profitLossReport.total_profit >= 0 ? '#10b981, #059669' : '#f59e0b, #d97706'})`, color: 'white' }}>
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Toplam Kar</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>${profitLossReport.total_profit?.toLocaleString()}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>%{profitLossReport.profit_margin_percentage?.toFixed(1)} marj</div>
                    </div>
                    <ArrowUpRight size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
                
                <div className="card" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: 'white' }}>
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Ödeme Durumu</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                        <span style={{ color: '#4ade80' }}>{profitLossReport.paid_count}</span> / 
                        <span style={{ color: '#fbbf24' }}> {profitLossReport.pending_count}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>ödenen / bekleyen</div>
                    </div>
                    <DollarSign size={32} style={{ opacity: 0.5 }} />
                  </div>
                </div>
              </div>
            )}

            {/* Kategori Bazlı Kar/Zarar */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ margin: 0 }}>
                  <PieChart size={18} style={{ marginRight: '0.5rem' }} />
                  Kategori Bazlı Kar/Zarar
                </h3>
                <button 
                  className="btn btn-success" 
                  onClick={() => {
                    const csvContent = [
                      ['Kar/Zarar Raporu'],
                      [`Dönem: ${dateRange.start} - ${dateRange.end}`],
                      [''],
                      ['Kategori', 'Satış ($)', 'Maliyet ($)', 'Kar ($)', 'İşlem Sayısı'],
                      ...(profitLossReport?.by_category || []).map(c => [
                        c.category_name, c.revenue, c.cost, c.profit, c.count
                      ]),
                      [''],
                      ['Tedarikçi Bazlı'],
                      ['Tedarikçi', 'Satış ($)', 'Maliyet ($)', 'Kar ($)', 'İşlem Sayısı'],
                      ...(profitLossReport?.by_supplier || []).map(s => [
                        s.supplier, s.revenue, s.cost, s.profit, s.count
                      ])
                    ].map(row => row.join(',')).join('\n');
                    
                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `kar_zarar_${dateRange.start}_${dateRange.end}.csv`;
                    link.click();
                    toast.success('Excel raporu indirildi');
                  }}
                >
                  <FileSpreadsheet size={16} /> Excel İndir
                </button>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Kategori</th>
                        <th style={{ textAlign: 'right' }}>Satış</th>
                        <th style={{ textAlign: 'right' }}>Maliyet</th>
                        <th style={{ textAlign: 'right', color: '#10b981' }}>Ödenen</th>
                        <th style={{ textAlign: 'right', color: '#f59e0b' }}>Ödenecek</th>
                        <th style={{ textAlign: 'right' }}>Kar</th>
                        <th style={{ textAlign: 'right' }}>Kar Marjı</th>
                        <th style={{ textAlign: 'center' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitLossReport?.by_category?.map((cat, idx) => (
                        <tr key={idx}>
                          <td>
                            <strong>{cat.category_name}</strong>
                          </td>
                          <td style={{ textAlign: 'right', color: '#3b82f6', fontWeight: 600 }}>
                            ${cat.revenue?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#ef4444' }}>
                            ${cat.cost?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#10b981' }}>
                            ${(cat.paid_cost || 0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#f59e0b' }}>
                            ${(cat.pending_cost || 0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: cat.profit >= 0 ? '#10b981' : '#f59e0b', fontWeight: 700 }}>
                            ${cat.profit?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {cat.revenue > 0 ? `%${((cat.profit / cat.revenue) * 100).toFixed(1)}` : '-'}
                          </td>
                          <td style={{ textAlign: 'center' }}>{cat.count}</td>
                        </tr>
                      ))}
                      {/* Toplam Satırı */}
                      {profitLossReport?.by_category?.length > 0 && (
                        <tr style={{ background: '#f8fafc', fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                          <td>TOPLAM</td>
                          <td style={{ textAlign: 'right', color: '#3b82f6' }}>
                            ${profitLossReport.total_revenue?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#ef4444' }}>
                            ${profitLossReport.total_cost?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#10b981' }}>
                            ${(profitLossReport.total_paid_cost || 0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: '#f59e0b' }}>
                            ${(profitLossReport.total_pending_cost || 0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', color: profitLossReport.total_profit >= 0 ? '#10b981' : '#f59e0b' }}>
                            ${profitLossReport.total_profit?.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            %{profitLossReport.profit_margin_percentage?.toFixed(1)}
                          </td>
                          <td style={{ textAlign: 'center' }}>{profitLossReport.items_count}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Tedarikçi Bazlı ve Aylık Analiz */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Tedarikçi Bazlı */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>
                    <Users size={18} style={{ marginRight: '0.5rem' }} />
                    Tedarikçi Bazlı Kar/Zarar
                  </h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tedarikçi</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Satış</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Maliyet</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Kar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitLossReport?.by_supplier?.slice(0, 10).map((sup, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}><strong>{sup.supplier}</strong></td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#3b82f6' }}>
                            ${sup.revenue?.toLocaleString()}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#ef4444' }}>
                            ${sup.cost?.toLocaleString()}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: sup.profit >= 0 ? '#10b981' : '#f59e0b' }}>
                            ${sup.profit?.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Aylık Trend */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>
                    <BarChart3 size={18} style={{ marginRight: '0.5rem' }} />
                    Aylık Kar/Zarar Trendi
                  </h3>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Ay</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Satış</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Maliyet</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Kar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitLossReport?.monthly_data?.map((month, idx) => {
                        const monthNames = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
                        const [year, monthNum] = month.month.split('-');
                        const monthName = `${monthNames[parseInt(monthNum)]} ${year}`;
                        
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem' }}><strong>{monthName}</strong></td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', color: '#3b82f6' }}>
                              ${month.revenue?.toLocaleString()}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', color: '#ef4444' }}>
                              ${month.cost?.toLocaleString()}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: month.profit >= 0 ? '#10b981' : '#f59e0b' }}>
                              ${month.profit?.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Veri yoksa mesaj */}
            {(!profitLossReport?.items_count || profitLossReport.items_count === 0) && (
              <div className="card" style={{ marginTop: '1.5rem' }}>
                <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
                  <DollarSign size={48} style={{ color: '#94a3b8', marginBottom: '1rem' }} />
                  <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Seçili Dönemde Veri Bulunamadı</h3>
                  <p style={{ color: '#94a3b8' }}>
                    {dateRange.start} - {dateRange.end} tarihleri arasında işlem takip kaydı bulunmuyor.
                  </p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                    İşlem takip kayıtları, fatura oluşturulduğunda otomatik olarak eklenir.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Raporlama;
